import Accessor from "@arcgis/core/core/Accessor";
import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import { when } from "@arcgis/core/core/reactiveUtils";
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import { getBaseUrl } from "../../utils/utils";
import {
  AppTypeInfo,
  EResourceType,
  ResourceInfo,
} from "../../widgets/AppTypeTile/AppTypeTile";
import { EAppTemplateType } from "templates-common-library/baseClasses/CompatibilityChecker";
import request from "@arcgis/core/request";
import PortalItem from "@arcgis/core/portal/PortalItem";
import NavBarViewModel from "src/app/ui/NavBar/NavBarViewModel";
import Telemetry from "@esri/telemetry";
import MyAppsViewModel from "../MyApps/MyAppsViewModel";
import AppT9N from "../../../t9n/App/App.json";
import { sanitizer } from "../../utils/appUtils";

export type RedirectOption = "goToConfigure" | "goToItemDetails" | null;

export interface PublishInfo {
  redirect: RedirectOption;
  title: string;
  folderId: string;
  tags: string;
  resourceInfo: ResourceInfo;
  appTypeInfo: AppTypeInfo;
}

export interface CopyInfo {
  newTitle: string;
  newTags: string;
  folder: string;
  appToCopyItem: __esri.PortalItem;
  appTypeInfo: AppTypeInfo;
  isOwner: boolean;
}

export interface UserFolder {
  id: string;
  title: string;
}

export enum EAppJSONTypeVals {
  webmap = "webmap",
  webscene = "webscene",
  group = "group",
}

export const AGOL_USER_ROOT_FOLDER_KEY = "root";

@subclass("Landing.data.PublishApp.PublishAppViewModel")
export default class PublishAppViewModel extends Accessor {
  @property()
  view: __esri.MapView | __esri.SceneView;

  @property()
  portal: __esri.Portal;

  @property()
  cred: __esri.Credential;

  @property()
  isPublishing: boolean = false;

  /** orgUrl with "http://"" replaced with "https://" */
  @property()
  orgUrl: string;

  /** portalUrl with "http://"" replaced with "https://" */
  @property()
  portalUrl: string;

  @property()
  folders: UserFolder[];

  @property({ readOnly: true })
  redirectionTarget: RedirectOption;

  @property()
  navbarVM: NavBarViewModel;

  @property()
  myAppsDataVM: MyAppsViewModel = new MyAppsViewModel();

  @property()
  telemetry: Telemetry;

  @property()
  appMessages: typeof AppT9N;

  private _baseUrl: string;
  private _loadingPageUrl: string;

  constructor(params?: any) {
    super(params);
    when(
      () => this?.appMessages !== undefined,
      () => {
        this._defineLinkUrl();
      },
      {
        initial: true,
        once: true,
      }
    );

    when(
      () => this?.portal !== undefined,
      async () => {
        await this.portal.load();
        this.orgUrl = getBaseUrl(this.portal);
        this.orgUrl = this.orgUrl.replace("http://", "https://");
        this.portalUrl = this.portal.url.replace("http://", "https://");

        // ::::: FOR DEVEXT ::::::::
        this._baseUrl = this.orgUrl;
        // ::::::::::::: FOR LOCALHOST TESTING :::::::::::::::
        // this._baseUrl = this.portalUrl;

        this.cred = await IdentityManager.checkSignInStatus(
          this.orgUrl + "/sharing"
        );

        this.folders = await this._getUserFolders();
        this.folders.unshift({
          id: AGOL_USER_ROOT_FOLDER_KEY,
          title: this.cred.userId,
        });
      },
      {
        initial: true,
        once: true,
      }
    );
  }

  ///////////////////////////
  /// Public Functions
  ///////////////////////////

  async createApp(
    info: PublishInfo,
    telemetryFunc: Function
  ): Promise<["success", string]> {
    let res;
    let appId;
    const newTab = window.open(sanitizer.sanitizeUrl(this._loadingPageUrl));
    try {
      telemetryFunc();
      this._set("redirectionTarget", info.redirect);

      this.isPublishing = true;
      res = await this._addItemRestCall(
        info,
        this._decideValuesForPublish(info)
      );
      if (res.success !== true || res.id == null) {
        throw new Error(`Add Item Failed: ${JSON.stringify(res)}`);
      }
      appId = res.id;
      await this.updateMetaData(info, appId, newTab);

      this.isPublishing = false;
      return ["success", appId];
    } catch (err) {
      // second attempt to update the metadata for the new item (from this issue: https://devtopia.esri.com/WebGIS/arcgis-template-landing/issues/689)
      try {
        if (appId != null) {
          await this.updateMetaData(info, appId, newTab);
        }
        this.isPublishing = false;

        return ["success", appId];
      } catch (err) {
        this.isPublishing = false;
        throw new Error(err);
      }
    }
  }

  /** update new item's metadata with appId, after it's created */
  updateMetaData = async (info: PublishInfo, appId: string, newTab: any) => {
    const updateRes = await this._updateItemRestCall(
      info.appTypeInfo,
      appId,
      info.folderId
    );
    if (updateRes.success !== true) {
      throw new Error(`Update Failed Again: ${JSON.stringify(updateRes)}`);
    }
    newTab.location.href =
      info.redirect === "goToConfigure"
        ? `${this.orgUrl}/apps/configure-template/index.html?appid=${appId}`
        : `${this.orgUrl}/home/item.html?id=${appId}`;
  };

  async copyApp(
    info: CopyInfo,
    telemetryFunc: Function
  ): Promise<["success", string]> {
    try {
      telemetryFunc();
      this.isPublishing = true;
      let res;

      if (info.isOwner) {
        res = await this._copyAppRestCall(info);
        if (res.success !== true) {
          throw new Error("Copy Failed");
        }
      } else {
        const itemData: any = await info.appToCopyItem.fetchData();
        info.folder = null;
        res = await this._addItemRestCall(
          {
            redirect: null,
            title: info.newTitle,
            folderId: info.folder,
            tags: info.newTags,
            resourceInfo: {
              resourceItem: info.appToCopyItem,
              resourceType: EResourceType.Unknown,
            },
            appTypeInfo: info.appTypeInfo,
          },
          itemData?.values
        );
        if (res.success !== true) {
          throw new Error("Create New for being a copy failed");
        }
      }
      const appId = res.itemId || res.id;

      const updateRes = await this._updateItemRestCall(
        info.appTypeInfo,
        appId,
        info.folder
      );
      if (updateRes.success !== true) {
        throw new Error("Update Failed");
      }

      const copiedItem = new PortalItem({
        id: appId,
      });

      await copiedItem.load();
      const copiedItemData = await copiedItem.fetchData();

      this.isPublishing = false;
      return ["success", appId];
    } catch (err) {
      this.isPublishing = false;
      throw new Error(err);
    }
  }

  async createAppFromExisting(
    info: PublishInfo,
    existingAppId: string,
    telemetryFunc: Function
  ) {
    const newTab = window.open(sanitizer.sanitizeUrl(this._loadingPageUrl));
    this._set("redirectionTarget", info.redirect);

    try {
      telemetryFunc();
      this.isPublishing = true;

      const existingApp = new PortalItem({
        id: existingAppId,
        portal: this.portal,
      });
      await existingApp.load();
      const existingAppData = await existingApp.fetchData();

      this._clearInValuesAndDraft(existingAppData?.values, "type");
      this._clearInValuesAndDraft(existingAppData?.values, "webmap");
      this._clearInValuesAndDraft(existingAppData?.values, "webscene");
      this._clearInValuesAndDraft(existingAppData?.values, "group");
      this._clearInValuesAndDraft(existingAppData?.values, "itemCollection");

      const valuesToMixin = this._decideValuesForPublish(info);
      existingAppData.values = {
        ...existingAppData.values,
        ...valuesToMixin,
      };

      const res = await this._addItemRestCall(info, existingAppData.values);
      if (res.success !== true) {
        throw new Error("create from existing Failed");
      }
      const appId = res.id;

      await this.updateMetaData(info, appId, newTab);
      this.isPublishing = false;

      return "success";
    } catch (err) {
      this.isPublishing = false;
      throw new Error(err);
    }
  }

  async checkCanDelete(
    appId: string
  ): Promise<{ itemId?: string; success: boolean }> {
    try {
      return this._canDeleteRestCall(appId);
    } catch (error) {
      console.error(error);
      return { success: false };
    }
  }

  async deleteItem(
    appId: string,
    permanentDelete: boolean
  ): Promise<boolean | string> {
    try {
      const res = this._deleteRestCall(appId, permanentDelete);
      return res;
    } catch (error) {
      return error;
    }
  }

  async doesTitleExist(appTitle: string, folderId: string): Promise<boolean> {
    const requestUrl = `title: "${appTitle}" AND type:"Web Mapping Application" AND owner:"${
      this.cred.userId
    }" AND ownerfolder:"${
      folderId == null ? AGOL_USER_ROOT_FOLDER_KEY : folderId
    }"`;

    let searchResults: __esri.PortalItem[] = [];
    let start = 1;
    let query = {
      query: requestUrl,
      num: 100,
    };

    while (start !== -1) {
      const res = await this.portal.queryItems(query);
      searchResults = searchResults.concat(res.results);
      start = res.nextQueryParams.start;
      query = res.nextQueryParams;
    }

    return (
      searchResults.filter((item: __esri.PortalItem) => {
        return item.title === appTitle;
      })?.length > 0
    );
  }

  getFolderName(folderId: string): string {
    return this.folders?.find((folder: UserFolder) => {
      return folderId === folder.id;
    })?.title;
  }

  ///////////////////////////
  /// Private Functions
  ///////////////////////////

  private async _getUserFolders(): Promise<UserFolder[]> {
    const userFolderUrl = `${this._baseUrl}/sharing/rest/content/users/${this.cred.userId}?num=1&f=json`;
    const res = await request(userFolderUrl);
    return res.data.folders;
  }

  private async _addItemRestCall(
    publishInfo: PublishInfo,
    values: any
  ): Promise<{ success: boolean; id: string; folder: string }> {
    const { userId } = this.cred;
    const { title, tags, appTypeInfo, folderId, resourceInfo } = publishInfo;
    const { thumbnailUrl, appTypeUrlFragment, appTypeId } = appTypeInfo;

    const addItemInfo = new FormData();
    addItemInfo.append("type", "Web Mapping Application");
    addItemInfo.append(
      "typeKeywords",
      "Web Map, Map, Online Map, Mapping Site, JavaScript, Ready To Use, configurableApp"
    );
    addItemInfo.append("url", `${this.orgUrl}${appTypeUrlFragment}`);
    addItemInfo.append("title", title);
    addItemInfo.append("tags", tags);
    addItemInfo.append(
      "text",
      JSON.stringify({ source: appTypeId, folderId: folderId, values: values })
    );
    addItemInfo.append("f", "json");

    let resourceThumbnailUrl =
      resourceInfo?.resourceItem?.getThumbnailUrl(1000);

    // extra step for Groups
    if (
      resourceThumbnailUrl == null &&
      resourceInfo?.resourceType === EResourceType.Group
    ) {
      const groupInfoUrl = `${this._baseUrl}/sharing/rest/community/groups/${resourceInfo.resourceItem.id}/?f=json`;
      const groupInfo = await request(groupInfoUrl);
      const thumnailFileName = groupInfo?.data?.thumbnail;
      if (thumnailFileName != null) {
        resourceThumbnailUrl = `${this._baseUrl}/sharing/rest/community/groups/${resourceInfo.resourceItem.id}/info/${thumnailFileName}?token=${this.cred.token}&w=1000`;
      }
    }

    if (resourceThumbnailUrl != null) {
      const thumbnailBlob = await fetch(resourceThumbnailUrl).then((res) =>
        res.blob()
      );
      addItemInfo.append("thumbnail", thumbnailBlob);
    } else {
      addItemInfo.append("thumbnailurl", thumbnailUrl);
    }

    if (folderId != null && folderId !== AGOL_USER_ROOT_FOLDER_KEY) {
      addItemInfo.append("folderId", folderId);
    }

    const res = await request(
      `${
        this._baseUrl
      }/sharing/rest/content/users/${userId}/${this._includeFolderId(
        folderId
      )}addItem`,
      {
        body: addItemInfo,
        method: "post",
      }
    );

    return res.data;
  }

  /**
   * 2nd AGOL REST call to update newly created item's url
   * @param appId - appId returned from 1st REST call
   */
  private async _updateItemRestCall(
    appTypeInfo: AppTypeInfo,
    appId: string,
    folderId: string
  ): Promise<{ success: boolean; id: string }> {
    const { userId } = this.cred;

    const updateItemInfo = {
      id: appId,
      url: `${this.orgUrl}${appTypeInfo.appTypeUrlFragment}?appid=${appId}`,
      f: "json",
    };

    if (folderId != null && folderId !== AGOL_USER_ROOT_FOLDER_KEY) {
      updateItemInfo["folderId"] = folderId;
    }

    const res = await request(
      `${
        this._baseUrl
      }/sharing/rest/content/users/${userId}/${this._includeFolderId(
        folderId
      )}items/${appId}/update`,
      {
        query: updateItemInfo,
        method: "post",
      }
    );

    return res.data;
  }

  private _decideValuesForPublish(publishInfo: PublishInfo): any {
    const { resourceItem, resourceType } = publishInfo.resourceInfo;

    // For "no-resource" workflow, or if a non-compat exists
    if (
      resourceType == EResourceType.None ||
      publishInfo.appTypeInfo.compatibleMsgs != null
    ) {
      return {};
    }

    // Special Cases for particular Templates
    const appType: EAppTemplateType =
      publishInfo.appTypeInfo.appTypeUrlFragment;
    if (
      appType === EAppTemplateType.Portfolio &&
      resourceType !== EResourceType.Group
    ) {
      const { id, title, itemUrl, description } =
        resourceItem as __esri.PortalItem;

      const finalItemUrl =
        this.orgUrl != null && id != null
          ? `${this.orgUrl}/sharing/rest/content/items/${id}`
          : itemUrl;

      const type =
        resourceType === EResourceType.Webmap
          ? "Web Map"
          : resourceType === EResourceType.Webscene
          ? "Web Scene"
          : null;
      return {
        itemCollection: [
          {
            addType: "itemBrowser",
            description,
            editorID: `${new Date().getTime()}`,
            id,
            title,
            type,
            url: finalItemUrl,
            visible: true,
          },
        ],
      };
    } else if (appType === EAppTemplateType.Manager) {
      const { id, title, itemUrl } = resourceItem as __esri.PortalItem;

      const finalItemUrl =
        this.orgUrl != null && id != null
          ? `${this.orgUrl}/sharing/rest/content/items/${id}`
          : itemUrl;

      const type =
        resourceType === EResourceType.Webmap
          ? "Web Map"
          : resourceType === EResourceType.Webscene
          ? "Web Scene"
          : null;
      return {
        mapItemCollection: [
          {
            chooseVisibleFields: false,
            enableRemoveAttachments: false,
            enableZoom: false,
            id,
            title,
            type,
            url: finalItemUrl,
            visible: true,
          },
        ],
      };
    } else if (
      appType === EAppTemplateType.Atlas &&
      resourceType !== EResourceType.Group
    ) {
      const { id } = resourceItem as __esri.PortalItem;

      return {
        contentSourceType: "content",
        atlasMapIds: [id],
      };
    } else {
      // Resource Type
      if (resourceType === EResourceType.Webmap) {
        return {
          webmap: resourceItem.id,
          type: EAppJSONTypeVals.webmap,
        };
      } else if (resourceType === EResourceType.Webscene) {
        return {
          webscene: resourceItem.id,
          type: EAppJSONTypeVals.webscene,
        };
      } else if (resourceType === EResourceType.Group) {
        return {
          group: resourceItem.id,
          type: EAppJSONTypeVals.group,
        };
      } else {
        // any fall through cases then we don't know what to do with them, just return { }
        return {};
      }
    }
  }

  private _includeFolderId(folderId: string): string {
    return folderId != null && folderId !== AGOL_USER_ROOT_FOLDER_KEY
      ? `${folderId}/`
      : "";
  }

  private async _copyAppRestCall(info: CopyInfo) {
    const { userId } = this.cred;
    const { newTitle, newTags, folder, appToCopyItem } = info;

    const copyItemInfo = {
      title: newTitle,
      tags: newTags,
      folder: this._includeFolderId(folder).replace("/", ""),
      includeResources: true,
      copyPrivateResources: true,
      f: "json",
    };

    const res = await request(
      `${this._baseUrl}/sharing/rest/content/users/${userId}/items/${appToCopyItem.id}/copy`,
      {
        query: copyItemInfo,
        method: "post",
      }
    );

    return res.data;
  }

  private async _canDeleteRestCall(
    appId: string
  ): Promise<{ itemId: string; success: boolean }> {
    const { userId } = this.cred;

    const info = {
      f: "json",
    };
    let res;
    try {
      res = await request(
        `${this._baseUrl}/sharing/rest/content/users/${userId}/items/${appId}/canDelete`,
        {
          query: info,
          method: "post",
        }
      );
    } catch (err) {
      return { itemId: appId, success: false };
    }

    return res.data;
  }

  private async _deleteRestCall(
    appId: string,
    permanentDelete: boolean
  ): Promise<boolean | string> {
    const { userId } = this.cred;
    const info = {
      f: "json",
      permanentDelete,
    };
    let res;
    try {
      res = await request(
        `${this._baseUrl}/sharing/rest/content/users/${userId}/items/${appId}/delete`,
        {
          query: info,
          method: "post",
        }
      );
    } catch (err) {
      return err;
    }

    return res.data.success;
  }

  private _defineLinkUrl(): void {
    const { pathname } = window.location;
    const distPath = pathname.substring(0, pathname.lastIndexOf("/"));
    this._loadingPageUrl = `${distPath}/assets/images/loading.html?msg=${this?.appMessages?.afterCreateStr}`;
  }

  private _clearInValuesAndDraft(valuesObj: any, prop: string): void {
    if (valuesObj == null) {
      return;
    }
    if (valuesObj?.draft?.[prop] != null) {
      valuesObj.draft[prop] = undefined;
    }
    if (valuesObj?.[prop] != null) {
      valuesObj[prop] = undefined;
    }
  }
}
